import {
  drillMorphemeSliceQueryFn,
  useGetDrillMorphemeSliceQueryKey,
  useGetMorphemePanelsSlicesQueries,
} from "@-/dev.improve.haniverse.lib.morpheme.read";
import {
  reviewCardFsrsResolutionQueryFn,
  useGetReviewCardFsrsResolutionQueryKey,
} from "@-/dev.improve.haniverse.lib.cards.study-card-data/use-review-card-fsrs-resolution-query";
import { type DrillType, isWordDrillKind } from "@-/dev.improve.haniverse.lib.drill.entities";
import {
  useGetWordCharacterBacksQueryKey,
  useGetWordContextSentencesQueryKey,
  wordCharacterBacksQueryFn,
  wordContextSentencesQueryFn,
} from "@-/dev.improve.haniverse.lib.sentence.context";
import { useCallback } from "react";
import { queryOptions } from "@-/com.npmjs.tanstack__react-query";
import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";

type ReviewCardMorphemePanel = "info" | "mnemonics";

export function useReviewCardMorphemePagePrefetch() {
  const getMorphemePanelsSlicesQueries = useGetMorphemePanelsSlicesQueries();
  const getDrillMorphemeSliceQueryKey = useGetDrillMorphemeSliceQueryKey();
  const getReviewCardFsrsResolutionQueryKey = useGetReviewCardFsrsResolutionQueryKey();
  const getWordCharacterBacksQueryKey = useGetWordCharacterBacksQueryKey();
  const getWordContextSentencesQueryKey = useGetWordContextSentencesQueryKey();
  const morphemeNoteFindFirstQueryFn = actorQueries(import.meta.location).morphemeNote.findFirst
    .queryFn;
  const getMorphemeNoteFindFirstQueryKey = actorQueries(
    import.meta.location,
  ).morphemeNote.findFirst.useGetQueryKey({});

  return useCallback(
    ({
      drillType,
      morpheme,
      panel = "info",
    }: {
      drillType: DrillType;
      morpheme: string;
      panel?: ReviewCardMorphemePanel;
    }) => {
      const {
        infoGraphemeMorphemeSliceQueryOptions,
        infoWordMorphemeSliceQueryOptions,
        mnemonicsPanelMorphemeSliceQueryOptions,
        mnemonicsPanelMorphemeUserSliceQueryOptions,
      } = getMorphemePanelsSlicesQueries(morpheme);

      // The drill page mounts exactly ONE branch gate per card (`DrillPageSuspense`), branching on
      // the SAME exported `isWordDrillKind` as this list, so each kind warms exactly what its gate
      // and faces read — a mismatch is silent (the page still renders, just via a suspense it was
      // supposed to make impossible):
      // - a grapheme drill: its drill slice + mnemonics slice (the gate) and the mnemonics user
      //   slice (the back's panel providers);
      // - a word drill: its characters' backs + sentence candidates (the gate), keyed on the word.
      //   Both key getters are called with their defaults, matching the read side
      //   (`wordContextSentences` folds its `limit` into the key).
      // Both read the FSRS resolution and the morpheme note.
      const branchQueries = isWordDrillKind(drillType.kind)
        ? [
            queryOptions({
              queryKey: getWordCharacterBacksQueryKey(morpheme),
              queryFn: wordCharacterBacksQueryFn,
            }),
            queryOptions({
              queryKey: getWordContextSentencesQueryKey(morpheme),
              queryFn: wordContextSentencesQueryFn,
            }),
          ]
        : [
            mnemonicsPanelMorphemeSliceQueryOptions,
            mnemonicsPanelMorphemeUserSliceQueryOptions,
            queryOptions({
              queryKey: getDrillMorphemeSliceQueryKey({ front: morpheme }),
              queryFn: drillMorphemeSliceQueryFn,
            }),
          ];
      const cardQueries = [
        ...branchQueries,
        queryOptions({
          queryKey: getReviewCardFsrsResolutionQueryKey(morpheme, drillType),
          queryFn: reviewCardFsrsResolutionQueryFn,
        }),
        queryOptions({
          queryKey: getMorphemeNoteFindFirstQueryKey({ where: { morpheme } }),
          queryFn: morphemeNoteFindFirstQueryFn,
        }),
      ];

      if (panel === "mnemonics") {
        return cardQueries;
      }
      return [
        infoGraphemeMorphemeSliceQueryOptions,
        infoWordMorphemeSliceQueryOptions,
        ...cardQueries,
      ];
    },
    [
      getDrillMorphemeSliceQueryKey,
      getMorphemeNoteFindFirstQueryKey,
      getMorphemePanelsSlicesQueries,
      getReviewCardFsrsResolutionQueryKey,
      getWordCharacterBacksQueryKey,
      getWordContextSentencesQueryKey,
      morphemeNoteFindFirstQueryFn,
    ],
  );
}
